import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, MessageCircle } from 'lucide-react';

/**
 * FAQSection — Netflix-style accordion FAQ.
 *
 * Visual reference: large dark cards stacked vertically, each with a
 * question on the left and a `+` icon on the right. Clicking a card
 * expands it to reveal the answer. Only one panel is open at a time;
 * clicking an open card closes it (toggle).
 *
 * Content is tailored to the Seven Chapter Books AI Reading
 * Intelligence Platform — every question is real and useful for a
 * first-time visitor.
 */

const FAQS = [
  {
    q: 'What is Seven Chapter Books?',
    a: (
      <>
        Seven Chapter Books is a curated reading platform where you can dive into a hand-picked
        catalog of e-books and audiobooks from independent authors. Each title comes with
        thoughtful reviews, a live reader discussion for the book, smart "what to read next"
        suggestions tailored to your taste, and tools that turn casual reading into a tracked,
        social, rewarding habit.{' '}
        <a
          href="/signup"
          className="text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-2 whitespace-nowrap font-medium"
          data-testid="faq-what-is-cta-start-reading"
        >
          Start Reading →
        </a>
      </>
    ),
  },
  {
    q: 'How much does a subscription cost?',
    a: (
      <>
        Reading on Seven Chapter Books is unlocked through a low monthly subscription that gives
        you unlimited access to the full catalog of e-books and audiobooks, plus every reader
        feature — reviews, the discussion rooms, recommendations, achievements and your personal
        leaderboard. You can cancel anytime; there&apos;s no contract. Pricing details are shown on
        the sign-up page.
      </>
    ),
  },
  {
    q: 'How do I read or listen to a book?',
    a: (
      <>
        Sign in, click any book on the home page or in your library, then hit <strong>Start Reading</strong> to
        open the immersive reader, or <strong>Listen</strong> if the title has an audiobook. Your progress
        is saved automatically across all devices, so you can pick up exactly where you left off — at home,
        on a phone, or on a tablet.
      </>
    ),
  },
  {
    q: 'Can I track my reading progress?',
    a: (
      <>
        Yes — your dashboard shows every book you&apos;ve started, your progress percentage, your
        reading streak, achievements you&apos;ve earned, and a personal leaderboard ranking you against
        other readers. Each completed book contributes to your profile&apos;s lifetime stats.
      </>
    ),
  },
  {
    q: 'Is there a community? Can I chat with other readers?',
    a: (
      <>
        Every book has its own live <strong>Book Discussion</strong> chat room and there&apos;s a
        platform-wide <strong>Community Chat</strong>. You can react to messages, share favorite
        passages, and follow what other readers are saying about a title in real time.
      </>
    ),
  },
  {
    q: 'Can I read on mobile? Is there an app?',
    a: (
      <>
        Seven Chapter Books is a fully responsive web app — open it in any modern browser on your
        phone, tablet, or laptop and the reader, library, and chat all adapt to your screen. Add it
        to your home screen for an app-like experience. A dedicated native app is on our roadmap.
      </>
    ),
  },
  {
    q: 'Are you an author? How can I publish here?',
    a: (
      <>
        We work directly with authors and small publishers. If you&apos;d like your title featured on
        Seven Chapter Books — including reviews, royalty reporting, and a featured launch slot —
        contact us at <a href="mailto:info@sevenchapterbooks.com" className="text-fuchsia-400 underline">info@sevenchapterbooks.com</a>.
      </>
    ),
  },
];

const FAQItem = ({ item, isOpen, onToggle, idx }) => (
  <li
    className={`border border-white/5 rounded-md transition-colors ${
      isOpen ? 'bg-zinc-800/95' : 'bg-zinc-800/70 hover:bg-zinc-800/90'
    }`}
    data-testid={`faq-item-${idx}`}
  >
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left text-white"
      aria-expanded={isOpen}
      aria-controls={`faq-panel-${idx}`}
      data-testid={`faq-question-${idx}`}
    >
      <span className="text-base sm:text-lg lg:text-xl font-medium">{item.q}</span>
      {isOpen ? (
        <X className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0 transition-transform" />
      ) : (
        <Plus className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0 transition-transform" />
      )}
    </button>
    <div
      id={`faq-panel-${idx}`}
      className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
    >
      <div className="overflow-hidden">
        <div
          className="px-6 pb-6 pt-1 text-sm sm:text-base text-gray-300 leading-relaxed border-t border-white/5"
          data-testid={`faq-answer-${idx}`}
        >
          {item.a}
        </div>
      </div>
    </div>
  </li>
);

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(0); // first item open by default
  const navigate = useNavigate();

  const toggle = (i) => setOpenIdx((cur) => (cur === i ? -1 : i));

  return (
    <section
      className="py-16 px-4 sm:px-6 lg:px-8 bg-black"
      data-testid="landing-faq-section"
    >
      <div className="max-w-screen-md mx-auto">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8">
          Frequently Asked Questions
        </h2>
        <ul className="space-y-3">
          {FAQS.map((item, i) => (
            <FAQItem
              key={i}
              idx={i}
              item={item}
              isOpen={openIdx === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </ul>
        <div className="text-center mt-10">
          <p className="text-gray-300 text-sm sm:text-base mb-4">
            Still have questions? We&apos;d love to hear from you.
          </p>
          <button
            type="button"
            onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            data-testid="faq-contact-cta"
          >
            <MessageCircle className="h-4 w-4" />
            Contact us
          </button>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
